import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

export const DiscountTotalAndRateChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const metrics = finance.dailyBreakdown![date]?.metrics || {};
            const discounts = Math.abs(Number(metrics.discounts) || 0);
            const grossSales = Number(metrics.grossSales) || 0;
            const discountRate = grossSales > 0
                ? Math.round((discounts / grossSales) * 10000) / 100
                : 0;

            return {
                date,
                formattedDate: format(parseISO(date), 'MMM dd'),
                discounts,
                discountRate,
            };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            yAxisId="left"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}%`}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, name: string) => {
                                if (name === 'discounts') {
                                    return [
                                        formatCompactNumber(value, currencySymbol),
                                        t('charts.discountAmount', 'Discount Amount'),
                                    ];
                                }
                                return [
                                    `${value}%`,
                                    t('charts.discountRate', 'Discount Rate'),
                                ];
                            }}
                            labelFormatter={(label: string) => label}
                        />
                        <Legend
                            formatter={(value: string) => {
                                if (value === 'discounts') return t('charts.discountAmount', 'Discount Amount');
                                return t('charts.discountRate', 'Discount Rate');
                            }}
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="discounts"
                            fill={CHART_COLORS.category.discounts}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="discountRate"
                            stroke={CHART_COLORS.chart.line1}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.chart.line1 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
